import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Card, Typography, Button, TextField, IconButton,
  Alert, CircularProgress, Skeleton, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControlLabel, Checkbox,
  Radio, RadioGroup, Select, MenuItem, FormControl
} from '@mui/material';
import { Add, Search, Close } from '@mui/icons-material';
import Layout from '../components/Layout';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { canUser } from '../utils/permissions';
import api from '../api/authApi';

const todayStr = () => new Date().toISOString().split('T')[0];
const fmt2 = v => parseFloat(v||0).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2});

export default function Deduction() {
  const { theme } = useApp();
  const { user } = useAuth();
  const canAdd = canUser(user, 'Deduction', 'add');

  const [types,      setTypes]      = useState([]);
  const [typeId,     setTypeId]     = useState('');
  const [rows,       setRows]       = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading,    setLoading]    = useState(false);
  const [search,     setSearch]     = useState('');
  const [page,       setPage]       = useState(1);
  const [pageSize,   setPageSize]   = useState(20);
  const [showAdd,    setShowAdd]    = useState(false);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');

  // Load deduction types on mount
  useEffect(() => {
    api.get('/deduction/types').then(res => {
      if (res.data?.success) {
        const t = res.data.data || [];
        setTypes(t);
        if (t.length > 0) setTypeId(t[0].typeId);
      }
    }).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    if (!typeId) return;
    setLoading(true);
    try {
      const res = await api.get('/deduction', { params: { typeId, search, page, pageSize } });
      if (res.data?.success) {
        setRows(res.data.data?.data || []);
        setTotalCount(res.data.data?.totalCount ?? 0);
      } else setError(res.data?.message || 'Load failed.');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [typeId, search, page, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const from = totalCount === 0 ? 0 : (page-1)*pageSize+1;
  const to   = Math.min(page*pageSize, totalCount);

  const thS = (align) => ({
    fontSize:'0.8rem', fontWeight:700, color:'#374151', padding:'10px 14px',
    borderBottom:'2px solid #e5e7eb', background:'#fff',
    whiteSpace:'nowrap', textAlign: align||'left',
  });
  const tdS = (bold, align, color) => ({
    fontSize:'0.82rem', padding:'9px 14px', borderBottom:'1px solid #f3f4f6',
    whiteSpace:'nowrap', fontWeight:bold?700:400,
    textAlign:align||'left', color:color||'#111827',
  });

  return (
    <Layout title="Deduction">
      {error   && <Alert severity="error"   sx={{ mb:2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb:2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Top bar */}
      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:2.5 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <Typography sx={{ fontSize:22 }}>💳</Typography>
          <Typography variant="h6" sx={{ fontWeight:800 }}>Deduction</Typography>
        </Box>
        {canAdd && <Button variant="contained" startIcon={<Add/>} onClick={() => setShowAdd(true)}
          sx={{ borderRadius:2, fontWeight:700, textTransform:'none', whiteSpace:'nowrap',
            background:`linear-gradient(135deg,${theme.primary},${theme.hover})` }}>
          + Add Other Payment Transaction
        </Button>}
      </Box>

      {/* Deduction Type selector */}
      <Box sx={{ display:'flex', alignItems:'center', gap:2, mb:2 }}>
        <Typography sx={{ fontSize:'0.88rem', fontWeight:600, color:'#374151' }}>
          Deduction Type :
        </Typography>
        <FormControl size="small" sx={{ minWidth:180 }}>
          <Select value={typeId} onChange={e => { setTypeId(e.target.value); setPage(1); }}
            sx={{ borderRadius:1.5, fontSize:'0.85rem', color:theme.primary, fontWeight:600,
              '& .MuiOutlinedInput-notchedOutline':{ borderColor:theme.primary },
              '&:hover .MuiOutlinedInput-notchedOutline':{ borderColor:theme.primary } }}>
            {types.map(t => (
              <MenuItem key={t.typeId} value={t.typeId} sx={{ fontSize:'0.85rem' }}>
                {t.typeName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Table card */}
      <Card sx={{ borderRadius:2, boxShadow:'0 1px 6px rgba(0,0,0,0.08)', overflow:'hidden' }}>
        {/* Toolbar */}
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between',
          px:2, py:1, background:theme.light, borderBottom:`1px solid ${theme.border}` }}>
          <Typography sx={{ fontWeight:700, color:theme.primary, fontSize:'0.88rem' }}>
            Deduction
          </Typography>
          <TextField size="small" placeholder="Search"
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            InputProps={{ startAdornment:<Search sx={{ fontSize:16, color:'#9ca3af', mr:0.5 }}/> }}
            sx={{ width:200, '& .MuiOutlinedInput-root':{ borderRadius:1.5, fontSize:'0.82rem', height:32 } }}/>
        </Box>

        {/* Table */}
        <Box sx={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...thS(), width:60 }}>Farmer Code</th>
                <th style={{ ...thS() }}>Farmer Name ↕</th>
                <th style={{ ...thS() }}>Phone Number ↕</th>
                <th style={{ ...thS('right') }}>Total Amount ↕</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({length:8}).map((_,i) => (
                    <tr key={i}>{[1,2,3,4].map(j => (
                      <td key={j} style={{ padding:'10px 14px' }}><Skeleton height={16}/></td>
                    ))}</tr>
                  ))
                : rows.length === 0
                  ? <tr><td colSpan={4} style={{ textAlign:'center', padding:'48px', color:'#9ca3af', fontSize:'0.85rem' }}>
                      No Records Found.
                    </td></tr>
                  : rows.map((row, idx) => (
                      <tr key={idx} style={{ background:idx%2===0?'#fff':'#fafafa',
                        cursor:'pointer', transition:'background 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background='#f0f9ff'}
                        onMouseLeave={e => e.currentTarget.style.background=idx%2===0?'#fff':'#fafafa'}>
                        <td style={tdS(true)}>{row.farmerCode}</td>
                        <td style={tdS(false)}>{row.farmerName}</td>
                        <td style={tdS(false,'left','#6b7280')}>{row.phoneNumber || '—'}</td>
                        <td style={tdS(false,'right', parseFloat(row.totalAmount)>0?'#dc2626':'#111827')}>
                          {fmt2(row.totalAmount)}
                        </td>
                      </tr>
                    ))
              }
            </tbody>
          </table>
        </Box>

        {/* Pagination */}
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center',
          gap:0.5, px:2, py:1.5, borderTop:'1px solid #f0f0f0', background:'#fafafa' }}>
          <Typography sx={{ fontSize:'0.78rem', color:'#6b7280', mr:2 }}>
            Showing {from} to {to} of {totalCount} entries
          </Typography>
          {[
            { lbl:'«', t:1,          d:page<=1 },
            { lbl:'‹', t:page-1,     d:page<=1 },
            { lbl:'›', t:page+1,     d:page>=totalPages },
            { lbl:'»', t:totalPages, d:page>=totalPages },
          ].map(({ lbl, t, d }) => (
            <IconButton key={lbl} size="small" disabled={d} onClick={() => setPage(t)}
              sx={{ width:28, height:28, border:'1px solid #e5e7eb', borderRadius:1 }}>
              <Typography sx={{ fontSize:12, fontWeight:700 }}>{lbl}</Typography>
            </IconButton>
          ))}
          <Box sx={{ mx:1, px:2, py:0.4, background:theme.primary, color:'#fff',
            borderRadius:1, fontSize:'0.78rem', fontWeight:700, textAlign:'center', minWidth:28 }}>
            {page}
          </Box>
          <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
            style={{ marginLeft:4, fontSize:'0.78rem', padding:'3px 6px',
              borderRadius:4, border:'1px solid #d1d5db', background:'#fff' }}>
            {[10,20,50,100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </Box>
      </Card>

      {showAdd && (
        <AddDeductionDialog
          open={showAdd}
          types={types}
          defaultTypeId={typeId}
          onClose={() => setShowAdd(false)}
          onSaved={() => {
            fetchData(); setShowAdd(false);
            setSuccess('Transaction added successfully.');
            setTimeout(() => setSuccess(''), 3000);
          }}
        />
      )}
    </Layout>
  );
}

// ── Add Deduction Dialog ──────────────────────────────────
function AddDeductionDialog({ open, types, defaultTypeId, onClose, onSaved }) {
  const { theme } = useApp();

  const [farmerCode,  setFarmerCode]  = useState('');
  const [farmerName,  setFarmerName]  = useState('');
  const typeId = defaultTypeId || '';
  const [date,        setDate]        = useState(todayStr());
  const [direction,   setDirection]   = useState('Received');
  const [amount,      setAmount]      = useState('');
  const [comments,    setComments]    = useState('');
  const [saveAndCont, setSaveAndCont] = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [searching,   setSearching]   = useState(false);
  const [error,       setError]       = useState('');

  const codeRef  = useRef('');
  const foundRef = useRef(false);
  useEffect(() => { codeRef.current = farmerCode; }, [farmerCode]);

  const lookupFarmer = async (code) => {
    if (!code) return;
    setSearching(true); setError('');
    try {
      const res = await api.get(`/collection/farmer/${code}`);
      if (res.data?.success && res.data?.data) {
        setFarmerName(res.data.data.farmerName);
        foundRef.current = true;
      } else {
        setFarmerName(''); foundRef.current = false;
        setError('Farmer not found.');
      }
    } catch { setError('Lookup failed.'); }
    finally { setSearching(false); }
  };

  const handleReset = () => {
    setFarmerCode(''); setFarmerName(''); setAmount('');
    setComments(''); setError(''); foundRef.current = false;
  };

  const handleSave = async () => {
    if (!farmerCode) { setError('Enter farmer code.'); return; }
    if (!farmerName) { setError('Farmer not found. Press search.'); return; }
    if (!amount)     { setError('Enter amount.'); return; }
    if (!comments)   { setError('Enter comments.'); return; }
    setSaving(true);
    try {
      const res = await api.post('/deduction', {
        farmerCode, typeId, date, direction,
        amount: parseFloat(amount),
        comments,
      });
      if (res.data?.success) {
        if (saveAndCont) handleReset();
        else onSaved();
      } else setError(res.data?.message || 'Save failed.');
    } catch (e) { setError(e.response?.data?.message || e.message || 'Save failed.'); }
    finally { setSaving(false); }
  };

  const lbSx = { fontSize:'0.82rem', fontWeight:600, color:'#374151', mb:0.5 };
  const inSx = { '& .MuiOutlinedInput-root':{ borderRadius:1.5, fontSize:'0.82rem' } };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx:{ borderRadius:2.5 } }}>
      <DialogTitle sx={{ py:1.5, px:2.5, fontWeight:700, fontSize:'0.95rem',
        background:`linear-gradient(135deg,${theme.primary},${theme.hover})`, color:'#fff',
        display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        Add Other Payment Transaction
        <IconButton size="small" onClick={onClose} sx={{ color:'#fff' }}>
          <Close sx={{ fontSize:18 }}/>
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px:2.5, pt:2.5, pb:1 }}>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb:1.5, py:0.5 }}>{error}</Alert>}

        {/* Farmer */}
        <Box sx={{ mb:1.8 }}>
          <Typography sx={lbSx}>Farmer <span style={{ color:'#dc2626' }}>*</span></Typography>
          <Box sx={{ display:'flex', gap:1 }}>
            <TextField size="small" fullWidth placeholder="Farmer code"
              value={farmerCode}
              onChange={e => { setFarmerCode(e.target.value.toUpperCase()); setFarmerName(''); foundRef.current=false; }}
              onKeyDown={e => { if(e.key==='Enter'){e.preventDefault(); lookupFarmer(codeRef.current.trim()); }}}
              onBlur={() => { if(codeRef.current.trim() && !foundRef.current) lookupFarmer(codeRef.current.trim()); }}
              sx={inSx}/>
            <IconButton onClick={() => lookupFarmer(codeRef.current.trim())} disabled={searching}
              sx={{ border:`1.5px solid ${theme.border}`, borderRadius:1.5, color:theme.primary }}>
              {searching ? <CircularProgress size={16}/> : <Search sx={{ fontSize:18 }}/>}
            </IconButton>
          </Box>
          {farmerName && (
            <Typography sx={{ fontSize:'0.75rem', color:theme.primary, mt:0.3, fontWeight:600 }}>
              ✅ {farmerName}
            </Typography>
          )}
        </Box>

        {/* Date */}
        <Box sx={{ mb:1.8 }}>
          <Typography sx={lbSx}>Date <span style={{ color:'#dc2626' }}>*</span></Typography>
          <TextField type="date" size="small" fullWidth value={date}
            onChange={e => setDate(e.target.value)}
            inputProps={{ max:todayStr() }} sx={inSx}/>
        </Box>

        {/* Given / Received */}
        <Box sx={{ mb:1.8 }}>
          <Typography sx={lbSx}>Given / Received <span style={{ color:'#dc2626' }}>*</span></Typography>
          <RadioGroup row value={direction} onChange={e => setDirection(e.target.value)}>
            <FormControlLabel value="Received" control={
              <Radio size="small" sx={{ color:theme.primary, '&.Mui-checked':{ color:theme.primary } }}/>}
              label={<Typography sx={{ fontSize:'0.82rem' }}>Received</Typography>}/>
            <FormControlLabel value="Given" control={
              <Radio size="small" sx={{ color:theme.primary, '&.Mui-checked':{ color:theme.primary } }}/>}
              label={<Typography sx={{ fontSize:'0.82rem' }}>Given</Typography>}/>
          </RadioGroup>
        </Box>

        {/* Amount */}
        <Box sx={{ mb:1.8 }}>
          <Typography sx={lbSx}>Amount <span style={{ color:'#dc2626' }}>*</span></Typography>
          <TextField type="number" size="small" fullWidth value={amount}
            onChange={e => setAmount(e.target.value)} sx={inSx}/>
        </Box>

        {/* Comments */}
        <Box sx={{ mb:1.5 }}>
          <Typography sx={lbSx}>Comments <span style={{ color:'#dc2626' }}>*</span></Typography>
          <TextField size="small" fullWidth multiline rows={2} value={comments}
            onChange={e => setComments(e.target.value)} sx={inSx}/>
        </Box>

        <FormControlLabel
          control={<Checkbox size="small" checked={saveAndCont} onChange={e => setSaveAndCont(e.target.checked)}
            sx={{ color:theme.primary, '&.Mui-checked':{ color:theme.primary } }}/>}
          label={<Typography sx={{ fontSize:'0.82rem' }}>Save And Add Another</Typography>}/>
      </DialogContent>

      <DialogActions sx={{ px:2.5, pb:2, gap:1 }}>
        <Button variant="outlined" onClick={handleReset}
          sx={{ borderRadius:1.5, textTransform:'none', borderColor:'#d1d5db', color:'#374151' }}>
          Reset
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          sx={{ borderRadius:1.5, textTransform:'none',
            background:`linear-gradient(135deg,${theme.primary},${theme.hover})` }}>
          {saving ? <CircularProgress size={16} sx={{ color:'#fff' }}/> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
